import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { orchard } from '../../theme';
import CherryFace from '../talk/CherryFace';
import { prefersReducedMotion } from './motion';

/* Celebrating correct answers in a row (quick assessment):
   - StreakBadge: a flame with the count, hotter as the streak grows;
   - StreakFx (an overlay over the card): sparks on every «знает», and at
     3, 5, 10, 15… in a row confetti, a big «N подряд!» and Черри popping up
     to cheer; a streak of 3+ that breaks ends with a puff and «серия N». */

const MILESTONES = new Set([3, 5, 10, 15, 20, 25, 30, 40, 50, 75, 100]);
const COLORS = [
  orchard.cherry,
  orchard.cherryBright,
  orchard.honey,
  orchard.leafBright,
  '#5BB6FF',
  '#B86BFF',
  '#FF9EB5',
];

const flicker = keyframes`
  0%, 100% { transform: scale(1, 1) rotate(-2deg); }
  25% { transform: scale(0.94, 1.08) rotate(2deg); }
  50% { transform: scale(1.05, 0.95) rotate(-1deg); }
  75% { transform: scale(0.97, 1.05) rotate(3deg); }
`;
const bump = keyframes`
  0% { transform: scale(0.6); }
  55% { transform: scale(1.35); }
  100% { transform: scale(1); }
`;
const banner = keyframes`
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.3) rotate(-8deg); }
  18% { opacity: 1; transform: translate(-50%, -50%) scale(1.18) rotate(3deg); }
  30% { transform: translate(-50%, -50%) scale(0.96) rotate(-1deg); }
  40%, 70% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
  100% { opacity: 0; transform: translate(-50%, -95%) scale(0.9); }
`;
const cheer = keyframes`
  0% { transform: translateY(130%) rotate(12deg); }
  22% { transform: translateY(-8%) rotate(-6deg); }
  32% { transform: translateY(2%) rotate(2deg); }
  40%, 75% { transform: translateY(0) rotate(0deg); }
  100% { transform: translateY(140%) rotate(10deg); }
`;
const plusOne = keyframes`
  0% { opacity: 0; transform: translate(-50%, 10px) scale(0.6); }
  25% { opacity: 1; transform: translate(-50%, -6px) scale(1.1); }
  100% { opacity: 0; transform: translate(-50%, -46px) scale(1); }
`;
const puff = keyframes`
  0% { opacity: 0.85; transform: translate(-50%, 0) scale(0.4); filter: blur(0); }
  100% { opacity: 0; transform: translate(-50%, -40px) scale(1.8); filter: blur(6px); }
`;
const fadeText = keyframes`
  0% { opacity: 0; transform: translate(-50%, 8px); }
  15%, 70% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -12px); }
`;

function tier(n) {
  if (n >= 15)
    return {
      a: '#B86BFF',
      b: '#5BB6FF',
      glow: 'rgba(130,110,255,0.65)',
      size: 1.3,
    };
  if (n >= 10)
    return {
      a: '#FF4D6D',
      b: '#B86BFF',
      glow: 'rgba(220,80,160,0.6)',
      size: 1.2,
    };
  if (n >= 5)
    return {
      a: '#FF5A1F',
      b: '#FFC233',
      glow: 'rgba(255,110,40,0.55)',
      size: 1.1,
    };
  return { a: '#FF8A1F', b: '#FFD25A', glow: 'rgba(255,160,40,0.45)', size: 1 };
}

function Flame({ n, size = 22 }) {
  const c = tier(n);
  const id = `fl${n >= 15 ? 4 : n >= 10 ? 3 : n >= 5 ? 2 : 1}`;
  return (
    <Box
      component="svg"
      viewBox="0 0 24 30"
      sx={{
        width: size * c.size,
        height: size * c.size * 1.25,
        transformOrigin: '50% 90%',
        animation: prefersReducedMotion()
          ? 'none'
          : `${flicker} ${n >= 10 ? 0.45 : 0.7}s ease-in-out infinite`,
        filter: `drop-shadow(0 0 ${n >= 5 ? 6 : 3}px ${c.glow})`,
      }}
    >
      <defs>
        <linearGradient id={id} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor={c.a} />
          <stop offset="100%" stopColor={c.b} />
        </linearGradient>
      </defs>
      <path
        d="M12 1C14 7 21 10 21 19A9 9 0 0 1 3 19C3 14 6 11 8 8C8 12 10 13 11 13C10 9 10 5 12 1Z"
        fill={`url(#${id})`}
      />
      <path
        d="M12 14C13.5 17 16 18 16 21.5A4 4 0 0 1 8 21.5C8 19 10 17.5 12 14Z"
        fill="#FFF3C4"
        opacity="0.9"
      />
    </Box>
  );
}

export function StreakBadge({ streak, en }) {
  if (streak < 2) return null;
  const hot = streak >= 3;
  return (
    <Box
      aria-label={en ? `${streak} in a row` : `${streak} подряд`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        pl: 0.5,
        pr: 1,
        height: 26,
        borderRadius: 999,
        fontWeight: 900,
        fontSize: '0.85rem',
        color: hot ? '#fff' : 'text.primary',
        background: hot
          ? `linear-gradient(135deg, ${tier(streak).a}, ${tier(streak).b})`
          : 'transparent',
        boxShadow: hot ? `0 4px 14px -4px ${tier(streak).glow}` : 'none',
        transition: 'background 0.4s ease, box-shadow 0.4s ease',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Flame n={streak} size={hot ? 16 : 14} />
      <Box
        key={streak}
        component="span"
        sx={{
          display: 'inline-block',
          animation: `${bump} 0.45s cubic-bezier(.2,1.4,.4,1) both`,
        }}
      >
        {streak}
      </Box>
      {hot && (
        <Box
          component="span"
          sx={{ fontWeight: 700, fontSize: '0.75rem', opacity: 0.95 }}
        >
          {en ? 'in a row' : 'подряд'}
        </Box>
      )}
    </Box>
  );
}

export function confetti(canvas, { count = 90, power = 1 } = {}) {
  const ctx = canvas.getContext('2d');
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const parts = [];
  for (let i = 0; i < count; i++) {
    const fromLeft = i % 2 === 0;
    const a = (fromLeft ? -60 : -120) + (Math.random() - 0.5) * 50;
    const sp = (7 + Math.random() * 9) * power;
    parts.push({
      x: fromLeft ? w * 0.12 : w * 0.88,
      y: h * 0.78,
      vx: Math.cos((a * Math.PI) / 180) * sp,
      vy: Math.sin((a * Math.PI) / 180) * sp,
      r: 4 + Math.random() * 5,
      rot: Math.random() * 6,
      vr: (Math.random() - 0.5) * 0.4,
      c: COLORS[i % COLORS.length],
      shape: i % 3,
      life: 0,
    });
  }
  let raf = 0;
  let last = performance.now();
  const step = (now) => {
    const dt = Math.min(2, (now - last) / 16.7);
    last = now;
    ctx.clearRect(0, 0, w, h);
    let alive = 0;
    parts.forEach((p) => {
      p.life += dt;
      p.vy += 0.32 * dt;
      p.vx *= 0.99;
      p.vy *= 0.99;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      const fade = Math.max(0, 1 - p.life / 150);
      if (p.y > h + 20 || fade <= 0) return;
      alive += 1;
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.c;
      if (p.shape === 0) ctx.fillRect(-p.r, -p.r * 0.45, p.r * 2, p.r * 0.9);
      else if (p.shape === 1) {
        ctx.beginPath();
        ctx.arc(0, 0, p.r * 0.6, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(0, -p.r);
        ctx.lineTo(p.r * 0.8, p.r * 0.7);
        ctx.lineTo(-p.r * 0.8, p.r * 0.7);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    });
    if (alive) raf = requestAnimationFrame(step);
    else ctx.clearRect(0, 0, w, h);
  };
  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

const PRAISE_RU = {
  3: 'Три подряд!',
  5: 'Пять подряд — супер!',
  10: 'Десять подряд — невероятно!',
  15: '15 подряд — легенда!',
  20: '20 подряд — чемпион!',
};
const PRAISE_EN = {
  3: 'Three in a row!',
  5: 'Five in a row — super!',
  10: 'Ten in a row — amazing!',
  15: '15 in a row — legend!',
  20: '20 in a row — champion!',
};

export default function StreakFx({ streak, en }) {
  const prev = useRef(streak);
  const canvasRef = useRef(null);
  const stopRef = useRef(null);
  const [spark, setSpark] = useState(null); // { id }
  const [party, setParty] = useState(null); // { id, n }
  const [broken, setBroken] = useState(null); // { id, n }

  useEffect(() => {
    const before = prev.current;
    prev.current = streak;
    const calm = prefersReducedMotion();
    if (streak > before) {
      setSpark({ id: Date.now() });
      if (MILESTONES.has(streak)) {
        setParty({ id: Date.now(), n: streak });
        if (!calm && canvasRef.current) {
          if (stopRef.current) stopRef.current();
          stopRef.current = confetti(canvasRef.current, {
            count: streak >= 10 ? 160 : streak >= 5 ? 120 : 80,
            power: streak >= 10 ? 1.25 : 1,
          });
        }
      }
    } else if (streak === 0 && before >= 3) {
      setBroken({ id: Date.now(), n: before });
    }
  }, [streak]);

  useEffect(() => () => stopRef.current && stopRef.current(), []);

  // clear finished effects
  useEffect(() => {
    if (!party) return undefined;
    const t = setTimeout(() => setParty(null), 2600);
    return () => clearTimeout(t);
  }, [party]);
  useEffect(() => {
    if (!broken) return undefined;
    const t = setTimeout(() => setBroken(null), 1900);
    return () => clearTimeout(t);
  }, [broken]);
  useEffect(() => {
    if (!spark) return undefined;
    const t = setTimeout(() => setSpark(null), 900);
    return () => clearTimeout(t);
  }, [spark]);

  const praise = party
    ? (en ? PRAISE_EN : PRAISE_RU)[party.n] ||
      (en ? `${party.n} in a row!` : `${party.n} подряд!`)
    : '';

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
      {spark && (
        <Box
          key={spark.id}
          sx={{
            position: 'absolute',
            left: '50%',
            top: '14%',
            fontWeight: 900,
            fontSize: '1.4rem',
            color: orchard.leafBright,
            textShadow: '0 2px 10px rgba(46,125,91,0.35)',
            animation: `${plusOne} 0.85s ease-out both`,
          }}
        >
          +1
        </Box>
      )}
      {party && (
        <>
          <Box
            key={`b${party.id}`}
            sx={{
              position: 'absolute',
              left: '50%',
              top: '42%',
              px: 3,
              py: 1.25,
              borderRadius: '22px',
              whiteSpace: 'nowrap',
              fontFamily: orchard.fontDisplay,
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2.2rem' },
              color: '#fff',
              background: `linear-gradient(135deg, ${tier(party.n).a}, ${tier(party.n).b})`,
              boxShadow: `0 20px 50px -18px ${tier(party.n).glow}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              animation: `${banner} 2.4s cubic-bezier(.2,.9,.3,1) both`,
            }}
          >
            <Flame n={party.n} size={28} />
            {praise}
          </Box>
          <Box
            key={`c${party.id}`}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 24 },
              bottom: 0,
              width: { xs: 96, sm: 130 },
              animation: `${cheer} 2.5s cubic-bezier(.2,.9,.3,1) both`,
            }}
          >
            <CherryFace
              emotion={party.n >= 10 ? 'joy' : 'excited'}
              size="100%"
              follow={false}
            />
          </Box>
        </>
      )}
      {broken && (
        <>
          <Box
            key={`p${broken.id}`}
            sx={{
              position: 'absolute',
              left: '50%',
              top: '10%',
              width: 46,
              height: 46,
              borderRadius: '50%',
              bgcolor: 'rgba(140,130,140,0.45)',
              animation: `${puff} 0.9s ease-out both`,
            }}
          />
          <Box
            key={`t${broken.id}`}
            sx={{
              position: 'absolute',
              left: '50%',
              top: '12%',
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              fontWeight: 800,
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              color: 'text.secondary',
              bgcolor: 'background.paper',
              boxShadow: '0 6px 18px -10px rgba(0,0,0,0.35)',
              animation: `${fadeText} 1.8s ease both`,
            }}
          >
            {en
              ? `Streak of ${broken.n} — great!`
              : `Серия ${broken.n} — отлично!`}
          </Box>
        </>
      )}
    </Box>
  );
}
